import { useCallback, useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { Check, FileDown, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { ResponseWithCitations } from '../../ResponseWithCitations'
import { Button } from '@deepguide-ai/ui/button'
import { dispatchApplyBlock } from '../../../lib/dispatch-apply-block'
import { openMainPanelOverlayAtom } from '@/stores/mainPanelOverlayStore'
import { workspaceIdAtom } from '@/stores/workspaceStore'

type ApplyAction = 'replace' | 'insert-after' | 'delete'

interface ProposedEdit {
  blockId: string
  action: ApplyAction
  label: string
  content: string
}

interface ProposeApplyBlocksMessageProps {
  part: any
}

const ACTION_ICON = {
  replace: FileDown,
  'insert-after': Plus,
  delete: Trash2,
} as const

const ACTION_LABEL = {
  replace: 'Apply',
  'insert-after': 'Insert',
  delete: 'Remove',
} as const

// Vercel AI SDK shapes: tool output may be `output` (string or object)
// depending on streaming state. We only render once the call resolves.
function parsePayload(part: any): any {
  if (part.state !== 'output-available') return null
  const raw = part.output
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function parsePolicySlug(payload: any): string | null {
  const slug = payload?.policySlug
  return typeof slug === 'string' && slug.trim() ? slug : null
}

function parseEdits(payload: any): ProposedEdit[] {
  const edits = payload?.edits
  if (!Array.isArray(edits)) return []
  return edits
    .filter(
      (e: any) =>
        e &&
        typeof e.blockId === 'string' &&
        typeof e.label === 'string' &&
        typeof e.content === 'string' &&
        (e.action === 'replace' ||
          e.action === 'insert-after' ||
          e.action === 'delete'),
    )
    .map((e: any) => ({
      blockId: e.blockId,
      action: e.action as ApplyAction,
      label: e.label,
      content: e.content,
    }))
}

export function ProposeApplyBlocksMessage({
  part,
}: ProposeApplyBlocksMessageProps) {
  const payload = useMemo(() => parsePayload(part), [part])
  const edits = useMemo(() => parseEdits(payload), [payload])
  const policySlug = useMemo(() => parsePolicySlug(payload), [payload])
  const [appliedKeys, setAppliedKeys] = useState<Set<string>>(() => new Set())
  const workspaceId = useAtomValue(workspaceIdAtom)
  const openOverlay = useSetAtom(openMainPanelOverlayAtom)

  const handleApply = useCallback(
    (edit: ProposedEdit, key: string) => {
      if (appliedKeys.has(key)) return
      dispatchApplyBlock({
        blockId: edit.blockId,
        action: edit.action,
        content: edit.content,
        label: edit.label,
        policySlug,
        onResult: (ok) => {
          if (ok) {
            setAppliedKeys((prev) => {
              const next = new Set(prev)
              next.add(key)
              return next
            })
          }
        },
        // No editor is mounted for this policy. If we know which policy the
        // edit targets, open it in the preview overlay so the user can
        // review and Apply in the UI (clicking Apply again now lands on the
        // mounted editor). The server-side persist path is deliberately
        // avoided — the human reviews the change in context first.
        onNoEditor:
          policySlug && workspaceId
            ? () => {
                openOverlay({
                  kind: 'system-detail',
                  id: `system-detail-policy-${policySlug}`,
                  source: 'chat',
                  payload: {
                    entityType: 'policy',
                    entityId: policySlug,
                    workspaceId,
                  },
                })
                toast.message(
                  'Opening the policy. Click Apply to apply this edit',
                )
              }
            : undefined,
      })
    },
    [appliedKeys, policySlug, openOverlay, workspaceId],
  )

  if (edits.length === 0) {
    if (part.state === 'output-available') {
      // Tool resolved but payload couldn't be parsed — surface a quiet notice
      // rather than silently rendering nothing.
      return (
        <div className="rounded-md border border-status-amber-border bg-status-amber-subtle px-3 py-2 text-[12px] text-status-amber-strong">
          Edit suggestion couldn't render. Ask Vera to retry.
        </div>
      )
    }
    return (
      <div className="rounded-md border border-border bg-background px-3 py-2 text-[12px] text-text-subtle">
        Preparing suggested edit…
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {edits.map((edit, i) => {
        const Icon = ACTION_ICON[edit.action]
        const buttonLabel = ACTION_LABEL[edit.action]
        const key = `${i}:${edit.blockId}`
        const isApplied = appliedKeys.has(key)

        return (
          <div
            key={key}
            className="rounded-md border border-border-divider bg-card overflow-hidden"
          >
            <div className="px-3 py-1.5 bg-background border-b border-border">
              <span className="text-[11px] font-medium text-text-body">
                {edit.label}
              </span>
            </div>
            {edit.action !== 'delete' ? (
              <div className="px-3 py-2 max-h-[200px] overflow-y-auto">
                <ResponseWithCitations
                  content={edit.content}
                  sources={[]}
                  className="text-[13px] leading-relaxed"
                />
              </div>
            ) : (
              <div className="px-3 py-2 text-[12px] text-text-placeholder italic">
                This section will be removed from the document.
              </div>
            )}
            {/* Action sits at the end of the card, where the reader lands
                after the proposed text — and matches the primary style the
                other chat actions use. */}
            <div className="flex justify-end border-t border-border px-3 py-2">
              {isApplied ? (
                <span className="flex items-center gap-1 px-1 text-[12px] font-medium text-status-green">
                  <Check className="h-3.5 w-3.5" />
                  Applied
                </span>
              ) : (
                <Button
                  size="sm"
                  className="h-7 bg-primary px-3 text-[12px] hover:bg-primary/90"
                  onClick={() => handleApply(edit, key)}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {buttonLabel}
                </Button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
